import React, { useId, useMemo } from 'react';
import ReactSelect from 'react-select';

/**
 * Select reutilizable: modo nativo (`native`) o react-select (búsqueda / múltiple).
 *
 * @param {Object} props
 * @param {boolean} [props.native=false]
 * @param {Array<{value: string|number, label: string, disabled?: boolean}>} props.options
 */
const BaseSelect = ({
  id,
  name,
  label,
  options = [],
  value,
  onChange,
  onBlur,
  placeholder = 'Seleccione una opción',
  required = false,
  error,
  helperText,
  disabled = false,
  loading = false,
  valid = false,
  native = false,
  isMulti = false,
  isSearchable = true,
  isClearable = false,
  size = 'md',
  className = '',
  wrapperClassName = '',
  menuPortalTarget,
  noOptionsMessage = () => 'Sin opciones',
  ...rest
}) => {
  const generatedId = useId();
  const selectId = id || `ui-select-${generatedId}`;
  const errorId = `${selectId}-error`;
  const helperId = `${selectId}-helper`;

  const stateClass = error ? 'ui-select-native--invalid' : valid ? 'ui-select-native--valid' : '';

  const nativeValue = useMemo(() => {
    if (value == null || value === '') return '';
    if (isMulti && Array.isArray(value)) return value;
    return String(value);
  }, [value, isMulti]);

  const handleNativeChange = (e) => {
    if (!onChange) return;
    onChange({
      target: { name, value: e.target.value },
      currentTarget: { name, value: e.target.value },
    });
  };

  const selectedReactValue = useMemo(() => {
    if (native) return null;
    if (isMulti) {
      const vals = Array.isArray(value) ? value : [];
      return options.filter((o) => vals.includes(o.value));
    }
    return options.find((o) => String(o.value) === String(value)) || null;
  }, [native, isMulti, value, options]);

  const handleReactChange = (selected) => {
    if (!onChange) return;
    if (isMulti) {
      const vals = (selected || []).map((o) => o.value);
      onChange({ target: { name, value: vals } });
      return;
    }
    onChange({ target: { name, value: selected?.value ?? '' } });
  };

  const selectStyles = useMemo(
    () => ({
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      control: (base, state) => ({
        ...base,
        minHeight: size === 'sm' ? 36 : size === 'lg' ? 52 : 44,
        fontSize: size === 'sm' ? '0.8125rem' : '0.9375rem',
        borderColor: error ? '#f87171' : state.isFocused ? '#3b82f6' : '#e2e8f0',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.12)' : 'none',
        backgroundColor: error ? '#fef2f2' : disabled ? '#f8fafc' : '#fff',
        '&:hover': {
          borderColor: error ? '#f87171' : '#cbd5e1',
        },
      }),
    }),
    [error, disabled, size]
  );

  return (
    <div className={`ui-field ${wrapperClassName}`.trim()}>
      {label && (
        <label htmlFor={selectId} className="ui-field__label">
          {label}
          {required && <span className="ui-field__required">*</span>}
        </label>
      )}

      {native ? (
        <select
          id={selectId}
          name={name}
          value={nativeValue}
          onChange={handleNativeChange}
          onBlur={onBlur}
          disabled={disabled || loading}
          required={required}
          aria-invalid={Boolean(error)}
          aria-describedby={[error ? errorId : null, helperText ? helperId : null].filter(Boolean).join(' ') || undefined}
          className={['ui-select-native', stateClass, className].filter(Boolean).join(' ')}
          {...rest}
        >
          <option value="">{placeholder}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : (
        <div
          className={[
            'ui-select',
            error ? 'ui-select--invalid' : '',
            disabled || loading ? 'ui-select--disabled' : '',
            className,
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <ReactSelect
            inputId={selectId}
            name={name}
            options={options}
            value={selectedReactValue}
            onChange={handleReactChange}
            onBlur={onBlur}
            placeholder={placeholder}
            isDisabled={disabled || loading}
            isMulti={isMulti}
            isSearchable={isSearchable}
            isClearable={isClearable}
            isLoading={loading}
            classNamePrefix="ui-select"
            styles={selectStyles}
            menuPortalTarget={menuPortalTarget || (typeof document !== 'undefined' ? document.body : null)}
            noOptionsMessage={noOptionsMessage}
            {...rest}
          />
        </div>
      )}

      <div className="ui-field__error-slot">
        {error ? (
          <span id={errorId} className="ui-field__error" role="alert">
            {error}
          </span>
        ) : null}
      </div>

      {helperText && !error && (
        <p id={helperId} className="ui-field__helper">
          {helperText}
        </p>
      )}
    </div>
  );
};

export default BaseSelect;
